import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Calendar,
  CalendarDays,
  CheckCircle2,
  ChevronLeft,
  Crown,
  Dice5,
  DollarSign,
  Flame,
  Gamepad2,
  Gift,
  Loader2,
  Lock,
  Smartphone,
  Target,
  Trophy,
  Users,
  Wallet,
} from 'lucide-react';
import api from '../../services/api';
import { openInstallPrompt } from '../../components/InstallPrompt';

const ICON_MAP = {
  Calendar,
  CalendarDays,
  Target,
  Trophy,
  Dice5,
  Gamepad2,
  Wallet,
  Users,
  Flame,
  DollarSign,
  Gift,
  Crown,
  Smartphone,
};

const KIND_CONFIG = {
  daily: {
    title: 'Daily Tasks',
    subtitle: 'Reset every day at midnight',
    headerIcon: Calendar,
    accent: '#10B981',
    accentDark: '#059669',
    border: 'rgba(16,185,129,0.45)',
    cssClass: 'task-icon-pulse',
    emptyHint: 'No daily tasks are available right now.',
  },
  weekly: {
    title: 'Weekly Tasks',
    subtitle: 'Reset every Monday',
    headerIcon: CalendarDays,
    accent: '#F59E0B',
    accentDark: '#B45309',
    border: 'rgba(245,158,11,0.45)',
    cssClass: 'task-icon-flame',
    emptyHint: 'No weekly tasks are available right now.',
  },
};

const numberValue = (value, fallback = 0) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const formatNumber = (value) => {
  const number = numberValue(value);
  return Number.isInteger(number) ? number.toLocaleString() : number.toLocaleString(undefined, { maximumFractionDigits: 2 });
};

const normalizeTask = (task, fallbackAccent) => {
  const currentValue = numberValue(task.current_value);
  const targetValue = Math.max(numberValue(task.target_value, 1), 0.01);
  const rewardValue = numberValue(task.reward_value ?? task.reward_fp ?? task.reward_sc);
  const progressPct = Math.max(0, Math.min(100, numberValue(task.progress_pct, (currentValue / targetValue) * 100)));
  const claimed = Boolean(task.claimed_this_period ?? task.claimed);
  const completed = Boolean(task.completed ?? currentValue >= targetValue);
  const rewardType = task.reward_type === 'wallet' ? 'wallet' : 'fp';
  return {
    ...task,
    current_value: currentValue,
    target_value: targetValue,
    reward_value: rewardValue,
    reward_type: rewardType,
    reward: task.reward || (rewardType === 'wallet'
      ? `+$${formatNumber(rewardValue)} Main Wallet`
      : `+${formatNumber(rewardValue)} FP`),
    progress_pct: progressPct,
    completed,
    can_claim: Boolean(task.can_claim),
    claimed_this_period: claimed,
    accent: task.accent || fallbackAccent,
    icon: task.icon || 'Target',
  };
};

const sectionIsEnabled = (data, kind) => {
  const settings = data?.settings || {};
  const key = `${kind}_enabled`;
  const taskSetting = Array.isArray(data?.tasks)
    ? data.tasks.find((task) => task?.section_enabled !== undefined)?.section_enabled
    : undefined;
  return Boolean(
    settings[key]
    ?? settings[kind]?.enabled
    ?? data?.section_enabled
    ?? data?.enabled
    ?? taskSetting
    ?? true
  );
};

export default function TasksPage({ kind = 'daily' }) {
  const navigate = useNavigate();
  const { tenantSlug } = useParams();
  const explorePath = tenantSlug ? `/${tenantSlug}/app/explore` : '/app/explore';
  const cfg = KIND_CONFIG[kind] || KIND_CONFIG.daily;

  const [tasks, setTasks] = useState([]);
  const [sectionEnabled, setSectionEnabled] = useState(true);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [claimingId, setClaimingId] = useState('');
  const [earnedHasMore, setEarnedHasMore] = useState(false);
  const [earnedNextOffset, setEarnedNextOffset] = useState(0);
  const [earnedNextCursor, setEarnedNextCursor] = useState('');
  const [loadingOlder, setLoadingOlder] = useState(false);
  const loadGeneration = useRef(0);
  const olderLoadGeneration = useRef(0);
  const loadedOlderRewards = useRef(false);

  const loadTasks = useCallback(async ({ quiet = false } = {}) => {
    const generation = ++loadGeneration.current;
    if (!quiet) {
      olderLoadGeneration.current += 1;
      loadedOlderRewards.current = false;
      setLoadingOlder(false);
      setLoading(true);
    }
    try {
      const res = await api.get(`/api/tasks?kind=${kind}`);
      if (generation !== loadGeneration.current) return;
      const data = res?.data || {};
      const incoming = (data.tasks || []).map((task) => ({
        ...normalizeTask(task, cfg.accent),
        _loadedFromOlderRewards: false,
      }));
      setTasks((current) => {
        if (!quiet) return incoming;
        const byIdentity = new Map(
          incoming.map((task) => [task.eligibility_key || task.id, task]),
        );
        current.forEach((task) => {
          const identity = task.eligibility_key || task.id;
          if (
            task.earned_snapshot
            && !task.claimed_this_period
            && !byIdentity.has(identity)
          ) {
            byIdentity.set(identity, task);
          }
        });
        return Array.from(byIdentity.values());
      });
      setSectionEnabled(sectionIsEnabled(data, kind));
      if (!quiet || !loadedOlderRewards.current) {
        setEarnedHasMore(Boolean(data.earned_has_more));
        setEarnedNextOffset(numberValue(data.earned_next_offset));
        setEarnedNextCursor(data.earned_next_cursor || '');
      }
      setLoadError('');
    } catch (error) {
      if (generation !== loadGeneration.current) return;
      if (!quiet) setLoadError(error.response?.data?.detail || 'Tasks could not be loaded.');
    } finally {
      if (!quiet && generation === loadGeneration.current) setLoading(false);
    }
  }, [cfg.accent, kind]);

  const refreshEarnedEligibility = useCallback(async (identity) => {
    const res = await api.get(
      `/api/tasks/earned?kind=${kind}&eligibility_key=${encodeURIComponent(identity)}`,
    );
    const data = res.data || {};
    const refreshed = (data.tasks || []).find(
      (task) => task.eligibility_key === identity,
    );
    if (refreshed) {
      return {
        ...normalizeTask(refreshed, cfg.accent),
        _loadedFromOlderRewards: true,
      };
    }
    if (['claimed', 'missing'].includes(data.eligibility_status)) {
      return null;
    }
    // A rolling-deployment backend may ignore the new exact-lookup query and
    // return page one. Do not replace this identity unless the key matches.
    return undefined;
  }, [cfg.accent, kind]);

  useEffect(() => {
    loadTasks();
    return () => {
      loadGeneration.current += 1;
    };
  }, [loadTasks]);

  useEffect(() => {
    const hasClaimingRewards = tasks.some(
      (task) => task.claim_status === 'claiming',
    );
    if (!hasClaimingRewards) return undefined;
    const timer = window.setInterval(() => {
      loadTasks({ quiet: true });
    }, 15000);
    return () => window.clearInterval(timer);
  }, [loadTasks, tasks]);

  const olderClaimingSignature = JSON.stringify(
    tasks
      .filter(
        (task) => (
          task.claim_status === 'claiming'
          && task._loadedFromOlderRewards
          && task.eligibility_key
        ),
      )
      .map((task) => task.eligibility_key)
      .sort(),
  );

  useEffect(() => {
    const olderClaimKeys = JSON.parse(olderClaimingSignature);
    if (!olderClaimKeys.length) return undefined;
    let cancelled = false;
    const refreshOlderClaims = async () => {
      const results = await Promise.all(olderClaimKeys.map(async (identity) => {
        try {
          return {
            identity,
            refreshed: await refreshEarnedEligibility(identity),
          };
        } catch {
          return null;
        }
      }));
      if (cancelled) return;
      const updates = new Map();
      results.filter(Boolean).forEach(({ identity, refreshed }) => {
        if (refreshed !== undefined) {
          updates.set(identity, refreshed);
        }
      });
      if (!updates.size) return;
      setTasks((current) => current.flatMap((task) => {
        const identity = task.eligibility_key || task.id;
        if (!updates.has(identity)) return [task];
        const updated = updates.get(identity);
        return updated ? [updated] : [];
      }));
    };
    refreshOlderClaims();
    const timer = window.setInterval(refreshOlderClaims, 15000);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [olderClaimingSignature, refreshEarnedEligibility]);

  const loadOlderRewards = async () => {
    if (!earnedHasMore || loadingOlder) return;
    const operation = ++olderLoadGeneration.current;
    setLoadingOlder(true);
    try {
      const pageQuery = earnedNextCursor
        ? `cursor=${encodeURIComponent(earnedNextCursor)}`
        : `skip=${earnedNextOffset}`;
      const res = await api.get(
        `/api/tasks/earned?kind=${kind}&${pageQuery}&limit=50`,
      );
      if (operation !== olderLoadGeneration.current) return;
      const data = res.data || {};
      const incoming = (data.tasks || []).map((task) => ({
        ...normalizeTask(task, cfg.accent),
        _loadedFromOlderRewards: true,
      }));
      loadedOlderRewards.current = true;
      setTasks((current) => {
        const byIdentity = new Map(
          current.map((task) => [task.eligibility_key || task.id, task]),
        );
        incoming.forEach((task) => {
          byIdentity.set(task.eligibility_key || task.id, task);
        });
        return Array.from(byIdentity.values());
      });
      setEarnedHasMore(Boolean(data.has_more));
      setEarnedNextOffset(numberValue(data.next_offset, earnedNextOffset));
      setEarnedNextCursor(data.next_cursor || '');
    } catch (error) {
      if (operation !== olderLoadGeneration.current) return;
      toast.error(error.response?.data?.detail || 'Could not load older rewards');
    } finally {
      if (operation === olderLoadGeneration.current) {
        setLoadingOlder(false);
      }
    }
  };

  const claimReward = async (task) => {
    if (!task.can_claim || claimingId) return;
    const taskIdentity = task.eligibility_key || task.id;
    setClaimingId(taskIdentity);
    try {
      const eligibilityQuery = task.eligibility_key
        ? `?eligibility_key=${encodeURIComponent(task.eligibility_key)}`
        : '';
      const res = await api.post(`/api/tasks/${task.id}/claim${eligibilityQuery}`);
      const rewardType = res.data?.reward_type === 'wallet' ? 'wallet' : task.reward_type;
      const reward = numberValue(res.data?.reward_value ?? (rewardType === 'wallet' ? res.data?.reward_wallet : res.data?.reward_fp) ?? task.reward_value);
      toast.success(res.data?.message || `Claimed ${formatNumber(reward)} ${rewardType === 'wallet' ? 'Main Wallet' : 'FP'}!`, {
        description: rewardType === 'wallet'
          ? 'Your Main Wallet balance has been updated.'
          : 'Your Freeplay balance has been updated.',
      });
      setTasks((current) => current.flatMap((item) => {
        if ((item.eligibility_key || item.id) !== taskIdentity) return [item];
        if (item.earned_snapshot) return [];
        return [{
          ...item,
          can_claim: false,
          claimed_this_period: true,
          completed: true,
        }];
      }));
      await loadTasks({ quiet: true });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not claim this reward');
      let needsFullReload = false;
      if (task.earned_snapshot && task.eligibility_key) {
        try {
          const refreshed = await refreshEarnedEligibility(
            task.eligibility_key,
          );
          if (refreshed !== undefined) {
            setTasks((current) => current.flatMap((item) => {
              const identity = item.eligibility_key || item.id;
              if (identity !== taskIdentity) return [item];
              return refreshed ? [refreshed] : [];
            }));
          } else {
            needsFullReload = true;
          }
        } catch {
          // The normal quiet refresh below remains the fallback.
        }
      }
      await loadTasks({ quiet: !needsFullReload });
    } finally {
      setClaimingId('');
    }
  };

  const HeaderIcon = cfg.headerIcon;

  return (
    <div
      className="player-app min-h-screen pb-12"
      style={{ color: '#FFFFFF' }}
      data-testid={`tasks-page-${kind}`}
    >
      <div
        className="sticky top-0 z-30 px-4 sm:px-6 py-4 flex items-center gap-3"
        style={{
          background: 'rgba(6,23,14,0.85)',
          backdropFilter: 'blur(18px) saturate(160%)',
          WebkitBackdropFilter: 'blur(18px) saturate(160%)',
          borderBottom: `1px solid ${cfg.border}`,
        }}
      >
        <button
          type="button"
          onClick={() => navigate(explorePath)}
          className="w-10 h-10 rounded-full flex items-center justify-center transition active:scale-95"
          style={{
            background: 'rgba(255,255,255,0.08)',
            border: '1px solid rgba(255,255,255,0.15)',
            color: '#FFFFFF',
          }}
          data-testid={`tasks-${kind}-back`}
          aria-label="Back"
        >
          <ChevronLeft className="w-5 h-5" />
        </button>
        <div className="flex-1">
          <h1 className="display font-extrabold text-xl sm:text-2xl flex items-center gap-2" style={{ color: '#FFFFFF' }}>
            <span
              className={`w-9 h-9 rounded-xl flex items-center justify-center ${cfg.cssClass}`}
              style={{
                background: `linear-gradient(135deg, ${cfg.accent}, ${cfg.accentDark})`,
                boxShadow: `0 6px 16px ${cfg.accent}88, inset 0 1px 0 rgba(255,255,255,0.45)`,
              }}
              aria-hidden
            >
              <HeaderIcon className="w-5 h-5 text-white" strokeWidth={2.6} />
            </span>
            {cfg.title}
          </h1>
          <p className="text-xs" style={{ color: 'rgba(255,255,255,0.7)' }}>{cfg.subtitle}</p>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8">
        {loading ? (
          <div className="flex items-center justify-center gap-2 text-sm py-12" style={{ color: 'rgba(255,255,255,0.65)' }}>
            <Loader2 className="w-4 h-4 animate-spin" />
            Loading tasks...
          </div>
        ) : loadError ? (
          <div
            className="rounded-3xl p-8 text-center"
            style={{ background: 'rgba(239,68,68,0.08)', border: '1px dashed rgba(239,68,68,0.4)' }}
            data-testid={`tasks-${kind}-error`}
          >
            <p className="text-sm text-red-200">{loadError}</p>
            <button
              type="button"
              onClick={() => loadTasks()}
              className="mt-4 rounded-xl px-4 py-2 text-sm font-bold bg-white/10 border border-white/10"
            >
              Try again
            </button>
          </div>
        ) : !sectionEnabled && tasks.length === 0 ? (
          <div
            className="rounded-3xl p-8 text-center"
            style={{ background: 'rgba(255,255,255,0.04)', border: `1px dashed ${cfg.border}` }}
            data-testid={`tasks-${kind}-disabled`}
          >
            <Lock className="w-10 h-10 mx-auto mb-3 opacity-50" style={{ color: cfg.accent }} />
            <p className="display font-extrabold">Tasks are currently off</p>
            <p className="text-sm mt-1" style={{ color: 'rgba(255,255,255,0.65)' }}>
              {kind === 'daily' ? 'Daily' : 'Weekly'} tasks will return when your team enables them.
            </p>
          </div>
        ) : tasks.length === 0 ? (
          <div
            className="rounded-3xl p-8 text-center"
            style={{ background: 'rgba(255,255,255,0.04)', border: `1px dashed ${cfg.border}` }}
          >
            <Target className="w-10 h-10 mx-auto mb-3 opacity-40" style={{ color: cfg.accent }} />
            <p className="text-sm" style={{ color: 'rgba(255,255,255,0.7)' }}>{cfg.emptyHint}</p>
          </div>
        ) : (
          <div className="space-y-3">
            {!sectionEnabled && (
              <div
                className="rounded-2xl px-4 py-3 text-sm"
                style={{
                  color: '#FDE68A',
                  background: 'rgba(245,158,11,0.1)',
                  border: '1px solid rgba(245,158,11,0.35)',
                }}
              >
                New {kind} tasks are off, but rewards you already earned remain claimable.
              </div>
            )}
            <ul className="space-y-3">
            {tasks.map((task) => {
              const Icon = ICON_MAP[task.icon] || Target;
              const claimed = task.claimed_this_period;
              const claimReady = task.can_claim && !claimed;
              const taskIdentity = task.eligibility_key || task.id;
              const progressLabel = task.verification_type === 'game_load_amount'
                ? `$${formatNumber(task.current_value)} / $${formatNumber(task.target_value)}`
                : `${formatNumber(task.current_value)} / ${formatNumber(task.target_value)}`;
              return (
                <li
                  key={taskIdentity}
                  className="rounded-[28px] p-4 relative overflow-hidden"
                  style={{
                    background: claimed
                      ? `${task.accent}12`
                      : 'rgba(255,255,255,0.04)',
                    border: `1px solid ${task.accent}${claimed || task.completed ? '88' : '55'}`,
                    boxShadow: `0 4px 12px ${task.accent}1F`,
                  }}
                  data-testid={`task-${task.eligibility_id || task.id}`}
                >
                  <div className="flex items-start gap-3 sm:gap-4">
                    <span
                      className="shrink-0 w-12 h-12 rounded-2xl flex items-center justify-center"
                      style={{
                        background: `${task.accent}22`,
                        border: `1.5px solid ${task.accent}`,
                      }}
                      aria-hidden
                    >
                      {claimed
                        ? <CheckCircle2 className="w-6 h-6" style={{ color: task.accent }} strokeWidth={2.6} />
                        : <Icon className="w-6 h-6" style={{ color: task.accent }} strokeWidth={2.4} />}
                    </span>

                    <div className="flex-1 min-w-0">
                      <div className="flex flex-col items-start gap-2 min-[380px]:flex-row min-[380px]:justify-between min-[380px]:gap-3">
                        <div className="min-w-0">
                          <p className="display break-words text-base font-extrabold leading-tight sm:text-lg" style={{ color: '#FFFFFF' }}>
                            {task.title}
                          </p>
                          <p className="mt-0.5 break-words text-xs leading-tight sm:text-sm" style={{ color: 'rgba(255,255,255,0.7)' }}>
                            {task.description}
                          </p>
                        </div>
                        <span
                          className="display max-w-full shrink-0 break-words rounded-2xl px-3 py-1.5 text-xs font-extrabold sm:text-sm"
                          style={{ background: task.accent, color: '#0B0F0C' }}
                        >
                          {task.reward_type === 'wallet'
                            ? `+$${formatNumber(task.reward_value)} Wallet`
                            : `+${formatNumber(task.reward_value)} FP`}
                        </span>
                      </div>

                      {task.eligibility_note && (
                        <p className="mt-2 rounded-xl border border-violet-400/25 bg-violet-400/10 px-3 py-2 text-xs leading-relaxed text-violet-100">
                          {task.eligibility_note}
                        </p>
                      )}

                      <div className="mt-3">
                        <div className="mb-1.5 flex flex-col items-start gap-1 min-[360px]:flex-row min-[360px]:items-center min-[360px]:justify-between min-[360px]:gap-3">
                          <span className="text-[10px] uppercase tracking-wider font-bold" style={{ color: 'rgba(255,255,255,0.58)' }}>
                            {claimed ? 'Reward claimed' : task.completed ? 'Completed' : 'Progress'}
                          </span>
                          <span className="max-w-full break-all text-xs font-semibold tabular-nums min-[360px]:text-right" style={{ color: task.accent }}>{progressLabel}</span>
                        </div>
                        <div className="h-2 rounded-full overflow-hidden" style={{ background: 'rgba(255,255,255,0.08)' }}>
                          <div
                            className="h-full rounded-full transition-all duration-500"
                            style={{
                              width: `${task.progress_pct}%`,
                              background: `linear-gradient(90deg, ${task.accent}, #FCD34D)`,
                            }}
                          />
                        </div>
                      </div>

                      <div className="mt-3 flex items-center justify-end">
                        {claimed ? (
                          <span
                            className="inline-flex items-center gap-1.5 rounded-xl px-3 py-2 text-xs font-extrabold"
                            style={{ color: task.accent, background: `${task.accent}18`, border: `1px solid ${task.accent}55` }}
                            data-testid={`task-claimed-${task.eligibility_id || task.id}`}
                          >
                            <CheckCircle2 className="w-4 h-4" />
                            Claimed
                          </span>
                        ) : claimReady ? (
                          <button
                            type="button"
                            onClick={() => claimReward(task)}
                            disabled={Boolean(claimingId)}
                            className="inline-flex items-center gap-2 rounded-xl px-4 py-2 text-xs sm:text-sm font-extrabold transition active:scale-[0.98] disabled:opacity-60"
                            style={{
                              color: '#0B0F0C',
                              background: `linear-gradient(135deg, ${task.accent}, #FCD34D)`,
                              boxShadow: `0 8px 18px ${task.accent}44`,
                            }}
                            data-testid={`task-claim-${task.eligibility_id || task.id}`}
                          >
                            {claimingId === taskIdentity
                              ? <Loader2 className="w-4 h-4 animate-spin" />
                              : <Gift className="w-4 h-4" />}
                            {claimingId === taskIdentity ? 'Claiming...' : 'Claim Rewards'}
                          </button>
                        ) : task.claim_status === 'claiming' ? (
                          <span className="inline-flex items-center gap-1.5 text-xs font-bold text-amber-300">
                            <Loader2 className="w-4 h-4 animate-spin" />
                            Reward processing
                          </span>
                        ) : task.completed ? (
                          <span className="inline-flex items-center gap-1.5 text-xs font-bold" style={{ color: task.accent }}>
                            <CheckCircle2 className="w-4 h-4" />
                            Completed
                          </span>
                        ) : (
                          task.verification_type === 'app_install' ? (
                            <button
                              type="button"
                              onClick={() => openInstallPrompt()}
                              className="inline-flex items-center gap-1.5 rounded-xl px-3 py-2 text-xs font-extrabold"
                              style={{ color: task.accent, background: `${task.accent}18`, border: `1px solid ${task.accent}55` }}
                              data-testid={`task-install-app-${task.id}`}
                            >
                              <Smartphone className="w-4 h-4" />
                              Install app to claim
                            </button>
                          ) : (
                            <span className="text-[10px] uppercase tracking-wider flex items-center gap-1" style={{ color: 'rgba(255,255,255,0.5)' }}>
                              Complete to claim
                            </span>
                          )
                        )}
                      </div>
                    </div>
                  </div>
                </li>
              );
            })}
            </ul>
            {earnedHasMore && (
              <div className="flex justify-center pt-2">
                <button
                  type="button"
                  onClick={loadOlderRewards}
                  disabled={loadingOlder}
                  className="inline-flex items-center gap-2 rounded-xl px-4 py-2 text-sm font-bold bg-white/10 border border-white/10 disabled:opacity-60"
                >
                  {loadingOlder && <Loader2 className="w-4 h-4 animate-spin" />}
                  {loadingOlder ? 'Loading...' : 'Load older earned rewards'}
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
